import isEqual from 'lodash-es/isEqual';
import { ReactElement, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { BehaviorSubject, fromEvent, merge, noop, Observable } from 'rxjs';
import { distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { isPhrasingContent } from '../../helpers/dom/content';
import {
  findAncestor,
  findNextNonWhitespaceSibling,
  findNextSiblingOrAuncle,
  findPreviousNonWhitespaceSibling,
  findPreviousSiblingOrAuncle,
  isElement,
  isText,
  isWhitespaceText,
  removeChildren,
} from '../../helpers/dom/node';
import { isBreakElement } from '../../helpers/dom/paragraph';
import {
  createPointAtStartOfNode,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
} from '../../helpers/dom/point';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { createConnectingService } from '../base/connectingService';
import { NULL_SERVICE, Service } from '../base/service';
import { ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { HtmlEditService } from '../html/htmlEditService';

const EMBED_PARENT_DATA_ATTRIBUTE = 'data-front-embed-parent';
const EMBED_DATA_ATTRIBUTE = 'data-front-embed';
const EMBED_DOM_SELECTOR = `div[${EMBED_DATA_ATTRIBUTE}]`;

export interface EmbedProps {
  onUpdateContent: (content: ReactElement) => void;
  onDeleteEmbed: () => void;
}

export interface EmbedDefinition {
  domNode: Element;
  renderEmbed: (domNode: Element, props: EmbedProps) => ReactNode;
}

export interface EmbedService extends Service {
  insertEmbed(content: ReactElement<any, string>): void;
  processEmbeds(definitions: ReadonlyArray<EmbedDefinition>): void;
  renderEmbeds(): ReactNode;
  removeEmbeds(): void;
}

export const NULL_EMBED_SERVICE: EmbedService = {
  ...NULL_SERVICE,
  insertEmbed: noop,
  processEmbeds: noop,
  renderEmbeds: () => null,
  removeEmbeds: noop,
};

export function createEmbedService(
  element$: Observable<Element | null>,
  editService: HtmlEditService,
  selectionService: ElementSelectionService,
): EmbedService {
  return createConnectingService(
    element$,
    () => NULL_EMBED_SERVICE,
    element => new ElementEmbedService(element, editService, selectionService),
    (_nextService, prevService) => prevService.removeEmbeds(),
  );
}

class ElementEmbedService extends ElementService<Element> implements EmbedService {
  constructor(element: Element, editService: HtmlEditService, selectionService: ElementSelectionService) {
    // This caches embed definitions from the last round of processEmbeds() so we don't have to re-query the DOM in renderEmbeds().
    const definitions$ = new BehaviorSubject<ReadonlyArray<EmbedDefinition>>([]);

    super(element, {
      // Emit changes when the embed definitions change.
      changes$: definitions$.pipe(
        // Skip the emit-on-subscribe from the BehaviorSubject.
        skip(1),
        distinctUntilChanged(
          (prevDefinitions, nextDefinitions) =>
            !isMissingEmbed(nextDefinitions) && isEqual(prevDefinitions, nextDefinitions),
        ),
      ),

      effects$: merge(
        fromEvent<KeyboardEvent>(element, 'keydown').pipe(
          ignoreDuringCompositions(element),
          tap(event => this.onKeyDown(event)),
        ),
      ),
    });

    this.definitions$ = definitions$;
    this.editService = editService;
    this.selectionService = selectionService;
  }

  private readonly definitions$: BehaviorSubject<ReadonlyArray<EmbedDefinition>>;
  private readonly editService: HtmlEditService;
  private readonly selectionService: ElementSelectionService;

  insertEmbed(embed: ReactElement<any, string>) {
    const html = ReactDOMServer.renderToStaticMarkup(embed);

    // We can insert React static markup without sanitizing.
    this.editService.insertCleanHtml(html);
  }

  processEmbeds(definitions: ReadonlyArray<EmbedDefinition>) {
    const validDefinitions = definitions.filter(({ domNode }) => {
      domNode.setAttribute(EMBED_PARENT_DATA_ATTRIBUTE, '');
      const embedParentText = getEmbedParentTextContent(domNode, this.document);
      if (embedParentText === '') {
        deleteEmbedParent(domNode);
        return false;
      }

      return true;
    });

    this.definitions$.next(validDefinitions);
  }

  renderEmbeds() {
    const definitions = this.definitions$.getValue();

    return definitions.map(definition => {
      const container = definition.domNode;
      const embedContainer = getNewEmbedContainer(container, this.document);

      return ReactDOM.createPortal(
        definition.renderEmbed(container, makeEmbedProps(container, embedContainer)),
        embedContainer,
      );
    });
  }

  removeEmbeds() {
    this.processEmbeds([]);
  }

  onKeyDown(event: KeyboardEvent) {
    // Don't do anything if the default is prevented.
    if (event.defaultPrevented) {
      return;
    }

    // Don't do anything if there's no selection.
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { isCollapsed, range } = selection;

    // Don't do anything if a range is selected.
    if (!isCollapsed) {
      return;
    }

    // Don't do anything if we're not at an embed parent boundary.
    const cursorData = this.findCursorBoundaryAndEmbedParent(range);
    if (!cursorData) {
      return;
    }

    switch (event.key) {
      case 'Enter':
        this.onEnter(event, cursorData);
        break;

      case 'Backspace':
        onBackspace(event, cursorData);
        break;

      case 'Delete':
        onDelete(event, cursorData);
        break;

      default:
        break;
    }
  }

  /** Insert line break outside of embed parent if hitting enter at the start or end of the embed parent. */
  onEnter(event: KeyboardEvent, cursorData: EmbedCursorData) {
    const newLine = this.document.createElement('div');
    newLine.appendChild(this.document.createElement('br'));

    if (cursorData.boundary === EmbedCursorBoundary.AT_START) {
      cursorData.embedParent.insertAdjacentElement('beforebegin', newLine);
      event.preventDefault();
      return;
    }

    if (cursorData.boundary === EmbedCursorBoundary.AT_END) {
      cursorData.embedParent.insertAdjacentElement('afterend', newLine);
      this.selectionService.moveToPoint(createPointAtStartOfNode(newLine));
      event.preventDefault();
    }
  }

  findCursorBoundaryAndEmbedParent(range: Range): EmbedCursorData | undefined {
    const embedParentAncestor = findEmbedParentAncestor(range.startContainer, this.element);
    if (embedParentAncestor) {
      // We're inside the embed parent.
      return findCursorBoundaryInsideEmbedParent(embedParentAncestor, range);
    }

    const nextEmbedParent = this.findEmbedParentRightAfterRange(range);
    if (nextEmbedParent) {
      // We're right before an embed parent.
      return { embedParent: nextEmbedParent, boundary: EmbedCursorBoundary.RIGHT_BEFORE };
    }

    const previousEmbedParent = this.findEmbedParentRightBeforeRange(range);
    if (previousEmbedParent) {
      // We're right after an embed parent.
      return { embedParent: previousEmbedParent, boundary: EmbedCursorBoundary.RIGHT_AFTER };
    }

    return undefined;
  }

  findEmbedParentRightAfterRange(range: Range): Element | undefined {
    const blockContainer = findAncestor(range.endContainer, node => !isPhrasingContent(node), this.element);
    if (!blockContainer) {
      return undefined;
    }

    const isAtEndOfBlockContainer = createPointWithEndOfRange(range).isAtEndOfNode(
      blockContainer,
      node => !findNextNonWhitespaceSibling(node),
      this.element,
    );

    if (!isAtEndOfBlockContainer && !isCursorOnEmptyLine(range)) {
      return undefined;
    }

    const nextSiblingOrAuncle = findNextSiblingOrAuncle(blockContainer, this.element);
    if (!isEmbedParent(nextSiblingOrAuncle)) {
      return undefined;
    }

    return nextSiblingOrAuncle;
  }

  findEmbedParentRightBeforeRange(range: Range): Element | undefined {
    const blockContainer = findAncestor(range.endContainer, node => !isPhrasingContent(node), this.element);
    if (!blockContainer) {
      return undefined;
    }

    const isAtStartOfBlockContainer = createPointWithStartOfRange(range).isAtStartOfNode(
      blockContainer,
      node => !findNextNonWhitespaceSibling(node),
      this.element,
    );

    if (!isAtStartOfBlockContainer && !isCursorOnEmptyLine(range)) {
      return undefined;
    }

    const previousSiblingOrAuncle = findPreviousSiblingOrAuncle(blockContainer, this.element);
    if (!isEmbedParent(previousSiblingOrAuncle)) {
      return undefined;
    }

    return previousSiblingOrAuncle;
  }
}

function getNewEmbedContainer(parent: Element, document: Document) {
  parent.querySelectorAll(EMBED_DOM_SELECTOR).forEach(container => {
    container.remove();
  });

  const embedContainer = document.createElement('div');
  embedContainer.setAttribute(EMBED_DATA_ATTRIBUTE, '');
  embedContainer.setAttribute('contenteditable', 'false');
  parent.appendChild(embedContainer);
  return embedContainer;
}

function makeEmbedProps(element: Element, embedContainer: Element) {
  const onUpdateContent = (content: ReactElement) => {
    removeChildren(element, child => child !== embedContainer);
    const contentHtml = ReactDOMServer.renderToStaticMarkup(content);
    element.insertAdjacentHTML('afterbegin', contentHtml);
  };
  const onDeleteEmbed = () => element.remove();

  element.appendChild(embedContainer);

  return { onUpdateContent, onDeleteEmbed };
}

function isMissingEmbed(definitions: ReadonlyArray<EmbedDefinition>) {
  return definitions.some(definition => definition.domNode.querySelector(EMBED_DOM_SELECTOR) === null);
}

function findNextNonWhitespaceOrEmbedSibling(node: Node): Node | null {
  let sibling = node.nextSibling;

  while (
    sibling &&
    (isWhitespaceText(sibling) || (isElement(sibling) && sibling.hasAttribute(EMBED_DATA_ATTRIBUTE)))
  ) {
    sibling = sibling.nextSibling;
  }

  return sibling;
}

function isEmbedParent(node: Node | null): node is Element {
  return isElement(node) && node.hasAttribute(EMBED_PARENT_DATA_ATTRIBUTE);
}

function findEmbedParentAncestor(node: Node, container: Node): Element | null {
  return findAncestor(node, isEmbedParent, container);
}

enum EmbedCursorBoundary {
  RIGHT_BEFORE,
  AT_START,
  AT_END,
  RIGHT_AFTER,
}
interface EmbedCursorData {
  embedParent: Element;
  boundary: EmbedCursorBoundary;
}

/** Don't allow the backspace key at the start of or right after the embed. */
function onBackspace(event: KeyboardEvent, cursorData: EmbedCursorData) {
  if (
    cursorData.boundary === EmbedCursorBoundary.AT_START ||
    cursorData.boundary === EmbedCursorBoundary.RIGHT_AFTER
  ) {
    event.preventDefault();
  }
}

/** Don't allow the delete key at the end of or right before the embed. */
function onDelete(event: KeyboardEvent, cursorData: EmbedCursorData) {
  if (
    cursorData.boundary === EmbedCursorBoundary.AT_END ||
    cursorData.boundary === EmbedCursorBoundary.RIGHT_BEFORE
  ) {
    event.preventDefault();
  }
}

function findCursorBoundaryInsideEmbedParent(
  embedParent: Element,
  range: Range,
): EmbedCursorData | undefined {
  if (
    createPointWithStartOfRange(range).isAtStartOfNode(
      embedParent,
      node => !findPreviousNonWhitespaceSibling(node),
      embedParent,
    )
  ) {
    // We're at the start of the embed parent.
    return { embedParent, boundary: EmbedCursorBoundary.AT_START };
  }

  if (
    createPointWithEndOfRange(range).isAtEndOfNode(
      embedParent,
      node => !findNextNonWhitespaceOrEmbedSibling(node),
      embedParent,
    )
  ) {
    // We're at the end of the embed parent.
    return { embedParent, boundary: EmbedCursorBoundary.AT_END };
  }

  // We're somewhere in the middle of the embed parent.
  return undefined;
}

function isCursorOnEmptyLine(range: Range) {
  return (
    range.startContainer === range.endContainer &&
    range.endContainer.childNodes.length === 1 &&
    isBreakElement(range.endContainer.firstChild)
  );
}

function getEmbedParentTextContent(node: Element, document: Document): string {
  let textContent = '';

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: candidateNode =>
      isElement(candidateNode) && candidateNode.hasAttribute(EMBED_DATA_ATTRIBUTE)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });

  do {
    if (isText(walker.currentNode)) {
      textContent += walker.currentNode.data;
    }
  } while (walker.nextNode());

  return textContent;
}

function deleteEmbedParent(element: Element) {
  // Replace the element with an empty line.
  element.insertAdjacentHTML('afterend', '<div><br /></div>');
  element.remove();
}
